import { AfterViewInit, Directive, ElementRef, inject, Input, OnChanges, OnInit, Renderer2 } from '@angular/core';

/**
 * A directive that adds custom styling to a Material Design spinner element
 * with the `ctrSpinnerHotel` selector.
 */
@Directive({
  selector: 'mat-spinner[ctrSpinnerHotel]',
  standalone: true,
})
export class SpinnerHotelDirective implements OnInit, OnChanges, AfterViewInit {
  /**
   * Whether to use the contrast color instead of the primary color.
   */
  @Input() useContrastColor = false;

  /**
   * The primary color used for the spinner.
   */
  primaryColor?: string;

  /**
   * The contrast color used for the spinner.
   */
  contrastColor?: string;

  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef<HTMLElement>);

  /**
   * Lifecycle hook that is called after the view has been initialized.
   * Sets the color of the spinner.
   */
  ngAfterViewInit(): void {
    this.setColor();
  }

  /**
   * Lifecycle hook that is called when any input property changes.
   * Sets the color of the spinner.
   */
  ngOnChanges(): void {
    this.setColor();
  }

  /**
   * Lifecycle hook that is called after the directive's data-bound properties have been initialized.
   * Retrieves the primary and contrast colors from the CSS variables.
   */
  ngOnInit(): void {
    this.primaryColor = getComputedStyle(document.body).getPropertyValue('--ctr-primary-color');
    this.contrastColor = getComputedStyle(document.body).getPropertyValue('--ctr-contrast-color');
  }

  /**
   * Sets the color of the spinner based on the `useContrastColor` property.
   */
  private setColor(): void {
    if (!this.elementRef?.nativeElement) return;
    const element = this.elementRef.nativeElement as HTMLElement;
    const styles = element.style.cssText.split(';');
    const newStyle = [
      ...styles,
      `--mat-progress-spinner-active-indicator-color: ${
        this.useContrastColor ? this.contrastColor : this.primaryColor
      };`,
    ].join(';');
    this.renderer.setProperty(element, 'style', newStyle);
  }
}
